import { useState, useRef, useMemo, useCallback } from "react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { cn } from "../../../../lib/utils";
import { useAudit } from "../../../../contexts/auditContext";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { getFileInputAcceptString } from "../../../../lib/fileTypes";
import { validateFilesWithToast } from "../../../../lib/fileUploadValidation";
import { ProcedureSessionSelectionModal } from "./ProcedureSessionSelectionModal";
import {
  EvidenceUploadModal,
  type ProcedureSessionInfo,
} from "./evidenceUpload/EvidenceUploadModal";
import { usePresignedUpload } from "../../../../hooks/usePresignedUpload";
import { UploadProgressOverlay } from "../../../../components/UploadProgressOverlay";
import { useHookCache } from "../../../../lib/hookCache";
import { UploadSourcePopover } from "./UploadSourcePopover";
import { HoverTooltip } from "../../../../components/HoverTooltip";
import { ExternalFilesImportDialog } from "./ExternalFilesImportDialog";
import {
  ExternalFile,
  useExternalFiles,
} from "../../../../hooks/useExternalFiles";

const SAMPLE_SPECIFIC_EVIDENCE_TOOLTIP =
  "Evidence for individual samples. Midship reads each file and matches it to the sample it belongs to.";

export const AuditUploadFile = ({
  sourceRowId,
  buttonText,
}: {
  sourceRowId?: string;
  buttonText?: string;
}) => {
  const {
    uploadDocumentsToControlSessionProcedures,
    controlSession,
    notifyDocumentUploadStarted,
  } = useAudit();
  const { invalidate } = useHookCache();
  const {
    state: presignedUploadState,
    uploadFiles: uploadFilesPresigned,
    cancel: cancelPresignedUpload,
    reset: resetPresignedUpload,
  } = usePresignedUpload();
  const {
    fetchExternalFiles,
    downloadFile,
    transferFilesToProcedures,
    files: externalFiles,
    loading: externalFilesLoading,
    downloading: externalDownloading,
  } = useExternalFiles({
    controlSessionId: controlSession?.id ?? null,
    notifyDocumentUploadStarted,
  });
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showZipModal, setShowZipModal] = useState(false);
  const [pendingZipFile, setPendingZipFile] = useState<File | null>(null);
  // Files selected alongside a zip, held until the auditor answers the
  // procedure question the zip needs asked first.
  const [looseFilesWithZip, setLooseFilesWithZip] = useState<File[]>([]);
  // The one procedure a pending zip is parsed against, answered before the
  // parse runs. The loose-file path needs no equivalent state: the selection
  // modal hands its answer straight to `handleModalConfirm`.
  const [zipParseProcedureSessionId, setZipParseProcedureSessionId] = useState<
    string | null
  >(null);
  const [pendingExternalFiles, setPendingExternalFiles] = useState<
    ExternalFile[]
  >([]);
  const [showExternalDialog, setShowExternalDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const externalFilesRequestedForControlIdRef = useRef<string | null>(null);
  const externalFilesLoadedForControlIdRef = useRef<string | null>(null);

  const controlId = controlSession?.control?.id ?? null;
  const externalRef = controlSession?.control?.externalRef ?? null;
  const hasExternalIntegration = Boolean(externalRef);

  const enabledProcedureSessions = useMemo(() => {
    if (!controlSession) return [];
    return controlSession.procedureSessions
      .filter((ps) => ps.procedure.isEnabled)
      .sort(
        (a, b) =>
          (a.procedure.procedureOrder || 0) - (b.procedure.procedureOrder || 0),
      );
  }, [controlSession]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files).filter((file) => {
        const fileName = file.name.toLowerCase();
        return !fileName.endsWith(".url");
      });

      if (files.length === 0) {
        toast.error("Please select valid files (URL files are not allowed)");
        return;
      }

      const validFiles = await validateFilesWithToast(files);

      if (validFiles.length === 0) return;

      // Check if there's a ZIP file
      const zipFile = validFiles.find((file) =>
        file.name.toLowerCase().endsWith(".zip"),
      );
      const nonZipFiles = validFiles.filter(
        (file) => !file.name.toLowerCase().endsWith(".zip"),
      );

      // Handle ZIP file separately with EvidenceUploadModal. `useEvidenceUpload`
      // branches its four routes on the session kind, so this path works for a
      // work step session as well as a control one.
      if (zipFile) {
        if (validFiles.length > 1) {
          // "Separately" reads as "a separate decision, later", which primes
          // exactly the wrong expectation on a multi-procedure control: the
          // next screen settles their destination too, and for them it is final.
          toast.warning(
            enabledProcedureSessions.length === 1
              ? "ZIP file detected. Other files will be uploaded separately."
              : `ZIP file detected. The other ${nonZipFiles.length} file${
                  nonZipFiles.length === 1 ? "" : "s"
                } will go to the procedure you pick next.`,
          );
        }

        // Ask which procedure BEFORE parsing, and ask for exactly one.
        //
        // A confirmed zip upload carries one `procedure_session_id`, so one is
        // all there is to answer. Answering first also means the archive is
        // only ever matched against one procedure's samples, which removes two
        // problems rather than guarding them: two procedures both numbering
        // their samples from 1 can no longer make a folder named `Sample 1`
        // ambiguous, and no folder can settle onto a sample the chosen
        // procedure does not own.
        //
        // The dialog still offers every enabled procedure, so switching later
        // works as before - that re-runs the parse against the new one.
        setPendingZipFile(zipFile);
        if (enabledProcedureSessions.length === 1) {
          // Nothing to ask.
          setZipParseProcedureSessionId(enabledProcedureSessions[0].id);
          setShowZipModal(true);
        } else {
          setPendingFiles([]);
          // Held, not dropped. The toast above promises these go up
          // separately, and this branch discarded them: a silent file loss
          // paired with a message saying the opposite.
          setLooseFilesWithZip(nonZipFiles);
          setShowModal(true);
        }

        // Handle non-zip files if any
        if (nonZipFiles.length > 0 && enabledProcedureSessions.length === 1) {
          await handleModalConfirm(
            [enabledProcedureSessions[0].id],
            nonZipFiles,
          );
        }
        return;
      }

      // If only one procedure session, auto-select it and skip modal
      if (enabledProcedureSessions.length === 1) {
        await handleModalConfirm([enabledProcedureSessions[0].id], validFiles);
      } else {
        // Open modal for procedure session selection
        setPendingFiles(validFiles);
        setShowModal(true);
      }
    }
  };

  const handleModalConfirm = async (
    procedureSessionIds: string[],
    filesToUpload?: File[],
  ) => {
    // A pending zip means this was the single-select question asked before the
    // parse. Hand the answer to the zip dialog, then send up whatever was
    // selected alongside the zip.
    //
    // Gated on `filesToUpload` being absent, not on `pendingZipFile` alone:
    // the recursive call below passes files explicitly and must reach the
    // upload, and so must the single-procedure path, which calls this with
    // `pendingZipFile` already set.
    if (pendingZipFile && filesToUpload === undefined) {
      const looseFiles = looseFilesWithZip;
      setLooseFilesWithZip([]);
      setZipParseProcedureSessionId(procedureSessionIds[0] ?? null);
      setShowModal(false);
      setShowZipModal(true);
      if (looseFiles.length > 0) {
        await handleModalConfirm(procedureSessionIds, looseFiles);
      }
      return;
    }

    // If we have pending external files, dispatch the transfer instead
    if (pendingExternalFiles.length > 0) {
      const filesToTransfer = pendingExternalFiles;
      setPendingExternalFiles([]);
      setShowModal(false);
      try {
        await transferFilesToProcedures(
          filesToTransfer,
          procedureSessionIds,
          true,
        );
      } catch (error) {
        console.error("Error transferring external files:", error);
      }
      return;
    }

    const files = filesToUpload ?? pendingFiles;
    if (files.length === 0) return;

    setUploading(true);
    try {
      // Try presigned upload first (for large files)
      const result = await uploadFilesPresigned(files);

      if (result.usedPresigned) {
        // Presigned upload was used - finalize with upload IDs
        await uploadDocumentsToControlSessionProcedures(
          files,
          procedureSessionIds,
          sourceRowId,
          true, // shouldMatchDocuments
          result.uploadIds,
        );
        resetPresignedUpload();
      } else {
        // Small files - use existing multipart flow
        await uploadDocumentsToControlSessionProcedures(
          files,
          procedureSessionIds,
          sourceRowId,
          true, // shouldMatchDocuments
        );
      }

      // Reset the input value to allow selecting the same file again
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      setPendingFiles([]);
    } catch (error) {
      // Don't show error toast for cancelled uploads
      if (error instanceof DOMException && error.name === "AbortError") {
        resetPresignedUpload();
        return;
      }
      console.error("Error uploading documents:", error);
      toast.error("Error uploading documents");
    } finally {
      setUploading(false);
    }
  };

  // Everything staged while a zip waits for its procedure answer. Both ways out
  // of that wait have to release it: the zip dialog closing, and the procedure
  // picker being dismissed before the dialog ever opens.
  //
  // The input value is part of it. A browser fires no `change` when the same
  // file is picked again unless the value was cleared, so leaving it set means
  // the auditor cannot retry the zip they just backed out of.
  const forgetPendingZip = useCallback(() => {
    setPendingZipFile(null);
    // Cleared so the next zip asks again rather than inheriting the procedure
    // this one was answered with.
    setZipParseProcedureSessionId(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, []);

  const handleZipUploadSuccess = useCallback(() => {
    forgetPendingZip();
    // Signal that an upload just started so the processing-status hook forces
    // the "Processing documents..." spinner on (matching the normal upload
    // path). Without this, zip uploads process silently with no feedback.
    notifyDocumentUploadStarted();
    // Refresh the control session envelope + status so new documents show up
    // (the per-procedure documents/source-rows caches re-fetch when the status
    // hook detects a transition out of `processing`).
    if (controlSession) {
      void invalidate(["control-session", controlSession.id]);
    }
  }, [
    controlSession,
    forgetPendingZip,
    invalidate,
    notifyDocumentUploadStarted,
  ]);

  const handleZipModalClose = useCallback(
    (open: boolean) => {
      setShowZipModal(open);
      if (!open) {
        forgetPendingZip();
      }
    },
    [forgetPendingZip],
  );

  // Dismissing the procedure picker abandons whatever it was asking about.
  // Confirming does not come through here, so the zip stays staged for the
  // dialog that is about to open.
  const handleSelectionModalCancel = useCallback(() => {
    setPendingExternalFiles([]);
    setLooseFilesWithZip([]);
    forgetPendingZip();
  }, [forgetPendingZip]);

  const uploadFiles = async (files: File[]) => {
    // If only one procedure session, auto-select it and skip modal
    if (enabledProcedureSessions.length === 1) {
      await handleModalConfirm([enabledProcedureSessions[0].id], files);
    } else {
      // Open modal for procedure session selection
      setPendingFiles(files);
      setShowModal(true);
    }
  };

  const triggerComputerFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const ensureExternalFilesLoaded = useCallback(async () => {
    if (!controlId) return;
    if (
      externalFilesLoadedForControlIdRef.current === controlId ||
      externalFilesRequestedForControlIdRef.current === controlId
    ) {
      return;
    }
    externalFilesRequestedForControlIdRef.current = controlId;
    const result = await fetchExternalFiles(controlId);
    if (result) {
      externalFilesLoadedForControlIdRef.current = controlId;
    }
    externalFilesRequestedForControlIdRef.current = null;
  }, [controlId, fetchExternalFiles]);

  const handleOpenOptroDialog = useCallback(() => {
    setShowExternalDialog(true);
    void ensureExternalFilesLoaded();
  }, [ensureExternalFilesLoaded]);

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      if (open && hasExternalIntegration) {
        void ensureExternalFilesLoaded();
      }
    },
    [ensureExternalFilesLoaded, hasExternalIntegration],
  );

  const handleExternalImport = useCallback(
    async (selected: ExternalFile[]) => {
      if (selected.length === 0) return;
      if (enabledProcedureSessions.length === 0) {
        toast.warning("No enabled procedures to import into");
        return;
      }
      if (enabledProcedureSessions.length === 1) {
        await transferFilesToProcedures(
          selected,
          [enabledProcedureSessions[0].id],
          true,
        );
        return;
      }
      // Multiple procedure sessions: defer to the picker
      setPendingExternalFiles(selected);
      setShowModal(true);
    },
    [enabledProcedureSessions, transferFilesToProcedures],
  );

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const validFiles = await validateFilesWithToast(files);
      if (validFiles.length === 0) return;
      await uploadFiles(validFiles);
    }
  };

  const buttonDisplayText = buttonText ?? "Sample-Specific Evidence";

  const hasSourceRowsInSession = useMemo(() => {
    return (
      controlSession?.procedureSessions?.some((ps) => ps.sourceRowsCount > 0) ??
      false
    );
  }, [controlSession]);

  // Envelope-shaped descriptors for the ZIP modal. The modal fetches the
  // matching source-rows slice for the currently-selected procedure session
  // via `useProcedureSession(psId)`; we no longer carry hydrated `sourceRows`
  // in this prop.
  // Every enabled procedure, not just the one the zip is parsed against. The
  // dialog's picker is how an auditor corrects the answer they gave before the
  // parse, so it has to list the alternatives.
  const procedureSessionsForZipModal = useMemo((): ProcedureSessionInfo[] => {
    if (!controlSession) return [];
    return controlSession.procedureSessions
      .filter((ps) => ps.procedure.isEnabled)
      .map((ps) => ({
        id: ps.id,
        // The procedure's name, not the session's. `session.name` defaults to
        // "New Session" and is rarely changed, so every option read "New
        // Session" - not a choice anyone can make. Agrees with the section
        // headings and the selection modal, which both use the procedure.
        name: ps.procedure.name,
      }));
  }, [controlSession]);

  const disabled = uploading || !hasSourceRowsInSession;

  const triggerButton = (
    <Button
      disabled={disabled}
      variant="outline"
      size="default"
      className={cn(isDragging ? "border-stone-400 bg-stone-100" : "")}
    >
      <Plus size={16} className="mr-2" />
      {uploading ? "Uploading..." : buttonDisplayText}
    </Button>
  );

  // Every file the answer will move, not just the archive. The loose files
  // are uploaded to the procedure picked here and CANNOT be re-routed
  // afterwards - switching procedure in the zip dialog only re-runs the
  // archive's mapping - so leaving them off this screen asked for their
  // destination without naming them. Same defect as the button that offered
  // "1 file" and processed three, one screen earlier.
  const procedureSessionModalDocumentNames = useMemo(() => {
    if (pendingExternalFiles.length > 0) {
      return pendingExternalFiles.map((f) => f.name ?? `File ${f.id}`);
    }
    if (pendingZipFile) {
      return [
        pendingZipFile.name,
        ...looseFilesWithZip.map((file) => file.name),
      ];
    }
    return pendingFiles.map((file) => file.name);
  }, [looseFilesWithZip, pendingExternalFiles, pendingFiles, pendingZipFile]);

  const procedureSessionModalDocumentCount =
    pendingExternalFiles.length > 0
      ? pendingExternalFiles.length
      : pendingZipFile
        ? 1 + looseFilesWithZip.length
        : pendingFiles.length;

  return (
    <>
      <div className="w-fit">
        <div
          className={cn(
            "relative transition-colors duration-200 h-10",
            isDragging ? "bg-stone-100 rounded-lg" : "",
          )}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {hasExternalIntegration ? (
            <UploadSourcePopover
              trigger={triggerButton}
              tooltip={SAMPLE_SPECIFIC_EVIDENCE_TOOLTIP}
              onUploadFromComputer={triggerComputerFilePicker}
              onImportFromOptro={handleOpenOptroDialog}
              onOpenChange={handlePopoverOpenChange}
            />
          ) : (
            <HoverTooltip content={SAMPLE_SPECIFIC_EVIDENCE_TOOLTIP}>
              <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={() => {
                  if (!disabled) triggerComputerFilePicker();
                }}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    triggerComputerFilePicker();
                  }
                }}
              >
                {triggerButton}
              </div>
            </HoverTooltip>
          )}
          <Input
            ref={inputRef}
            id="audit-upload-file"
            type="file"
            accept={getFileInputAcceptString() + ",.zip"}
            disabled={!hasSourceRowsInSession}
            multiple
            onChange={handleFileSelect}
            className="sr-only"
          />
        </div>
      </div>
      <ProcedureSessionSelectionModal
        open={showModal}
        onOpenChange={setShowModal}
        onCancel={handleSelectionModalCancel}
        onConfirm={handleModalConfirm}
        documentCount={procedureSessionModalDocumentCount}
        documentNames={procedureSessionModalDocumentNames}
        // A zip can only go to one procedure, so asking for a set would mean
        // discarding all but one of the answers.
        selectionMode={pendingZipFile ? "single" : "multiple"}
      />
      {controlSession && (
        <EvidenceUploadModal
          open={showZipModal}
          onOpenChange={handleZipModalClose}
          controlSessionId={controlSession.id}
          procedureSessions={procedureSessionsForZipModal}
          parseProcedureSessionId={zipParseProcedureSessionId}
          zipFile={pendingZipFile}
          onSuccess={handleZipUploadSuccess}
        />
      )}
      <ExternalFilesImportDialog
        open={showExternalDialog}
        onOpenChange={setShowExternalDialog}
        mode="sample"
        files={externalFiles}
        loading={externalFilesLoading}
        downloading={externalDownloading}
        onDownload={downloadFile}
        onImport={handleExternalImport}
      />
      <UploadProgressOverlay
        state={presignedUploadState}
        onCancel={cancelPresignedUpload}
      />
    </>
  );
};
